import { IVector2 } from "@formant/core/model/IVector2";
import { IVector3 } from "@formant/core/model/IVector3";

import { ClickShape } from "./ClickSignal";

export type Rect = Omit<DOMRectReadOnly, "toJSON">;

export const containsClick = (
    event: Readonly<ClickShape>,
    bounds: Rect,
): boolean =>
    event.clientX >= bounds.left &&
    event.clientX <= bounds.right &&
    event.clientY >= bounds.top &&
    event.clientY <= bounds.bottom;

export const getActualBounds = (
    bounds: Rect,
    contentAspectRatio: number,
): Rect => {
    const boundsAspectRatio = bounds.width / bounds.height;

    if (contentAspectRatio === boundsAspectRatio) {
        return bounds;
    }

    if (contentAspectRatio > boundsAspectRatio) {
        // Correct height for bounds.width
        const actualHeight = bounds.width * (1 / contentAspectRatio);
        const actualY = bounds.y - (actualHeight - bounds.height) / 2;
        return {
            x: bounds.x,
            y: actualY,
            width: bounds.width,
            height: actualHeight,
            bottom: actualY + actualHeight,
            left: bounds.x,
            right: bounds.x + bounds.width,
            top: actualY,
        };
    } else {
        // Correct width for bounds.height
        const actualWidth = bounds.height * contentAspectRatio;
        const actualX = bounds.x - (actualWidth - bounds.width) / 2;
        return {
            x: actualX,
            y: bounds.y,
            width: actualWidth,
            height: bounds.height,
            bottom: bounds.y + bounds.height,
            left: actualX,
            right: actualX + actualWidth,
            top: bounds.y,
        };
    }
};

export interface ITeleopCoordinateMapper {
    (
        event: ClickShape,
        bounds: Rect,
        contentDimensions: Readonly<IVector2>,
    ): IVector3 | false;
}

export const getMappedCoords: ITeleopCoordinateMapper = (
    event,
    bounds,
    { x: contentWidth, y: contentHeight },
) => {
    if (contentWidth <= 0 || contentHeight <= 0) {
        return false;
    }

    const contentAspectRatio = contentWidth / contentHeight;
    const actualBounds = getActualBounds(bounds, contentAspectRatio);

    if (!containsClick(event, actualBounds)) {
        return false;
    }

    return {
        x: (event.clientX - actualBounds.left) / actualBounds.width,
        y: (event.clientY - actualBounds.top) / actualBounds.height,
        z: 0,
    };
};

export const rejectInputAnd =
    (fn?: () => void): ITeleopCoordinateMapper =>
    () => {
        fn?.();
        return false;
    };

export const never = rejectInputAnd();
